import { seamUvEdges, uvEdges, type UvEdge, type UvLayout, type UvSelectMode, type UvSelection, type Vec2 } from '../../lib/uv-doc.js';
import type { UvOverlays, UvView } from '../../state/uv.js';

/**
 * Canvas 2D drawing of the UV Editor: view math (UV space ⇄ canvas pixels,
 * v pointing up like Blender), the 0..1 tile with its texture or checker,
 * the adaptive grid, faces (stretch heatmap, overlaps, selection), edges
 * (seams in red), vertices / face dots and pins.
 */

export function uvToScreen(view: UvView, size: Vec2, p: Vec2): Vec2 {
  return [size[0] / 2 + (p[0] - view.center[0]) * view.zoom, size[1] / 2 - (p[1] - view.center[1]) * view.zoom];
}

export function screenToUv(view: UvView, size: Vec2, s: Vec2): Vec2 {
  return [view.center[0] + (s[0] - size[0] / 2) / view.zoom, view.center[1] - (s[1] - size[1] / 2) / view.zoom];
}

/** Zoom about a canvas point (the point under the mouse stays put). */
export function zoomAt(view: UvView, size: Vec2, at: Vec2, factor: number): UvView {
  const zoom = Math.min(200000, Math.max(8, view.zoom * factor));
  const before = screenToUv(view, size, at);
  const next = { center: view.center, zoom };
  const after = screenToUv(next, size, at);
  return { center: [view.center[0] + before[0] - after[0], view.center[1] + before[1] - after[1]], zoom };
}

/** View showing `min..max` with a margin. */
export function frameView(min: Vec2, max: Vec2, size: Vec2): UvView {
  const w = Math.max(max[0] - min[0], 1e-3);
  const h = Math.max(max[1] - min[1], 1e-3);
  const zoom = Math.min(200000, Math.max(8, Math.min(size[0] / w, size[1] / h) * 0.86));
  return { center: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2], zoom };
}

/** Per-layout data the draw pass reuses (edges, seams). */
export interface DrawCache {
  layout: UvLayout;
  edges: UvEdge[];
  seams: [number, number][];
  pins: Set<number>;
}

export function drawCache(layout: UvLayout): DrawCache {
  return { layout, edges: uvEdges(layout.faces), seams: seamUvEdges(layout), pins: new Set(layout.pins) };
}

export interface DrawInput {
  size: Vec2;
  view: UvView;
  cache: DrawCache | null;
  /** UVs to draw (a transform preview), else the layout's. */
  uvs: readonly Vec2[] | null;
  mode: UvSelectMode;
  selection: UvSelection;
  overlays: UvOverlays;
  image: CanvasImageSource | null;
  imageSize: Vec2 | null;
  box: { a: Vec2; b: Vec2 } | null;
  /** Axis constraint line through the pivot while transforming. */
  constraint: { axis: 'u' | 'v'; pivot: Vec2 } | null;
}

const COLORS = {
  bg: '#1d1e22',
  tile: '#2a2c31',
  gridMinor: 'rgba(255,255,255,0.05)',
  gridMajor: 'rgba(255,255,255,0.12)',
  tileBorder: 'rgba(255,255,255,0.35)',
  edge: 'rgba(20,20,22,0.9)',
  edgeLight: 'rgba(225,228,235,0.75)',
  selected: '#ff9a2e',
  selectedFill: 'rgba(255,154,46,0.22)',
  faceFill: 'rgba(160,170,190,0.10)',
  readOnlyFill: 'rgba(120,150,210,0.12)',
  overlap: 'rgba(255,60,90,0.45)',
  seam: '#ff3b3b',
  pin: '#ff3030',
};

let checkerCanvas: HTMLCanvasElement | null = null;

/** Blender-like color grid: hue per column, light per row, checkered cells with labels. */
export function checkerTexture(): HTMLCanvasElement | null {
  if (checkerCanvas) return checkerCanvas;
  if (typeof document === 'undefined') return null;
  const size = 1024;
  const cells = 8;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const cell = size / cells;
  for (let x = 0; x < cells; x++) {
    for (let y = 0; y < cells; y++) {
      const hue = (x * 360) / cells;
      const light = 38 + (y % 2) * 10 + ((x + y) % 2) * 6;
      ctx.fillStyle = `hsl(${hue} 55% ${light}%)`;
      ctx.fillRect(x * cell, y * cell, cell, cell);
      // 4×4 checker inside each cell shows stretching.
      ctx.fillStyle = 'rgba(255,255,255,0.13)';
      const sub = cell / 4;
      for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) if ((i + j) % 2 === 0) ctx.fillRect(x * cell + i * sub, y * cell + j * sub, sub, sub);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = `bold ${Math.round(cell * 0.22)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${String.fromCharCode(65 + x)}${cells - y}`, x * cell + cell / 2, y * cell + cell / 2);
    }
  }
  checkerCanvas = c;
  return c;
}

/** Heatmap color for a distortion value 0 (blue) .. 1 (red). */
export function stretchColor(value: number, alpha = 0.62): string {
  const t = Math.min(1, Math.max(0, value));
  return `hsla(${Math.round(240 * (1 - t))}, 85%, 52%, ${alpha})`;
}

/** Grid spacing: halve from 1 UV unit until lines are ≥ 16 px apart (at most 1/4096). */
export function gridStep(zoom: number): number {
  let step = 1;
  while (step / 2 >= 1 / 4096 && (step / 2) * zoom >= 16) step /= 2;
  while (step * zoom < 16 && step < 1024) step *= 2;
  return step;
}

export function drawUv(ctx: CanvasRenderingContext2D, input: DrawInput): void {
  const { size, view, cache, overlays } = input;
  const [w, h] = size;
  const S = (p: Vec2) => uvToScreen(view, size, p);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, w, h);

  // 0..1 tile: texture, checker or plain.
  const [x0, y0] = S([0, 1]);
  const tile = view.zoom;
  ctx.fillStyle = COLORS.tile;
  ctx.fillRect(x0, y0, tile, tile);
  const background = overlays.background === 'checker' ? checkerTexture() : overlays.background === 'image' ? input.image : null;
  if (background) {
    const texW = overlays.background === 'image' && input.imageSize ? input.imageSize[0] : 1024;
    ctx.imageSmoothingEnabled = tile / Math.max(1, texW) < 2;
    ctx.globalAlpha = 0.9;
    ctx.drawImage(background, x0, y0, tile, tile);
    ctx.globalAlpha = 1;
    ctx.imageSmoothingEnabled = true;
  }

  if (overlays.grid) {
    const step = gridStep(view.zoom);
    const lo = screenToUv(view, size, [0, h]);
    const hi = screenToUv(view, size, [w, 0]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.strokeStyle = COLORS.gridMinor;
    for (let u = Math.ceil(lo[0] / step) * step; u <= hi[0]; u += step) {
      const x = Math.round(S([u, 0])[0]) + 0.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
    }
    for (let v = Math.ceil(lo[1] / step) * step; v <= hi[1]; v += step) {
      const y = Math.round(S([0, v])[1]) + 0.5;
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
    }
    ctx.stroke();
    ctx.beginPath();
    ctx.strokeStyle = COLORS.gridMajor;
    for (let u = Math.ceil(lo[0]); u <= hi[0]; u++) {
      const x = Math.round(S([u, 0])[0]) + 0.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
    }
    for (let v = Math.ceil(lo[1]); v <= hi[1]; v++) {
      const y = Math.round(S([0, v])[1]) + 0.5;
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
    }
    ctx.stroke();
  }
  ctx.strokeStyle = COLORS.tileBorder;
  ctx.lineWidth = 1;
  ctx.strokeRect(Math.round(x0) + 0.5, Math.round(y0) + 0.5, Math.round(tile), Math.round(tile));

  if (cache) drawLayout(ctx, input, cache, S);

  if (input.constraint) {
    const { axis, pivot } = input.constraint;
    const p = S(pivot);
    ctx.strokeStyle = axis === 'u' ? 'rgba(240,96,91,0.8)' : 'rgba(132,214,90,0.8)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (axis === 'u') {
      ctx.moveTo(0, p[1]);
      ctx.lineTo(w, p[1]);
    } else {
      ctx.moveTo(p[0], 0);
      ctx.lineTo(p[0], h);
    }
    ctx.stroke();
  }

  if (input.box) {
    const a = input.box.a;
    const b = input.box.b;
    ctx.setLineDash([4, 3]);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    const x = Math.min(a[0], b[0]);
    const y = Math.min(a[1], b[1]);
    ctx.fillRect(x, y, Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]));
    ctx.strokeRect(x + 0.5, y + 0.5, Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]));
    ctx.setLineDash([]);
  }
}

function drawLayout(ctx: CanvasRenderingContext2D, input: DrawInput, cache: DrawCache, S: (p: Vec2) => Vec2): void {
  const { overlays, selection, mode } = input;
  const layout = cache.layout;
  const uvs = input.uvs ?? layout.uvs;
  const points = uvs.map(S);
  const overlap = overlays.overlap ? new Set(layout.overlap) : null;
  const stretch = overlays.stretch === 'angle' ? layout.stretch : overlays.stretch === 'area' ? layout.areaStretch : null;

  const facePath = (face: readonly number[]) => {
    ctx.beginPath();
    face.forEach((i, k) => {
      const p = points[i];
      if (!p) return;
      if (k === 0) ctx.moveTo(p[0], p[1]);
      else ctx.lineTo(p[0], p[1]);
    });
    ctx.closePath();
  };

  // Faces: stretch heatmap / plain fill, selection tint, overlaps.
  layout.faces.forEach((face, f) => {
    if (face.length < 3) return;
    const selected = selection.faces.has(f);
    const fill = stretch && stretch.length === layout.faces.length
      ? stretchColor(stretch[f] ?? 0)
      : overlays.faces
        ? layout.editable
          ? COLORS.faceFill
          : COLORS.readOnlyFill
        : null;
    if (!fill && !selected && !overlap?.has(f)) return;
    facePath(face);
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (selected) {
      ctx.fillStyle = COLORS.selectedFill;
      ctx.fill();
    }
    if (overlap?.has(f)) {
      ctx.fillStyle = COLORS.overlap;
      ctx.fill();
    }
  });

  // Edges: dark under-stroke then light line, like Blender's outline style.
  ctx.lineJoin = 'round';
  for (const [style, width] of [
    [COLORS.edge, 3],
    [COLORS.edgeLight, 1],
  ] as const) {
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    ctx.beginPath();
    for (const e of cache.edges) {
      const a = points[e.a];
      const b = points[e.b];
      if (!a || !b) continue;
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
    }
    ctx.stroke();
  }

  if (overlays.seams && cache.seams.length) {
    ctx.strokeStyle = COLORS.seam;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (const [i, j] of cache.seams) {
      const a = points[i];
      const b = points[j];
      if (!a || !b) continue;
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
    }
    ctx.stroke();
  }

  // Selected edges on top.
  const facewise = mode === 'face' || mode === 'island';
  ctx.strokeStyle = COLORS.selected;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  for (const e of cache.edges) {
    const on = facewise ? e.faces.some((f) => selection.faces.has(f)) : selection.uvs.has(e.a) && selection.uvs.has(e.b);
    if (!on) continue;
    const a = points[e.a];
    const b = points[e.b];
    if (!a || !b) continue;
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
  }
  ctx.stroke();

  if (mode === 'vertex') {
    const used = new Set<number>();
    for (const face of layout.faces) for (const i of face) used.add(i);
    for (const pass of [false, true]) {
      ctx.fillStyle = pass ? COLORS.selected : '#e8e8ea';
      ctx.beginPath();
      for (const i of used) {
        if (selection.uvs.has(i) !== pass) continue;
        const p = points[i];
        if (!p) continue;
        ctx.rect(p[0] - 2, p[1] - 2, 4, 4);
      }
      ctx.fill();
    }
  } else if (facewise) {
    layout.faces.forEach((face, f) => {
      if (face.length < 3) return;
      let x = 0;
      let y = 0;
      for (const i of face) {
        x += points[i]?.[0] ?? 0;
        y += points[i]?.[1] ?? 0;
      }
      ctx.fillStyle = selection.faces.has(f) ? COLORS.selected : 'rgba(232,232,234,0.7)';
      ctx.fillRect(x / face.length - 1.5, y / face.length - 1.5, 3, 3);
    });
  }

  if (cache.pins.size) {
    ctx.fillStyle = COLORS.pin;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    for (const i of cache.pins) {
      const p = points[i];
      if (!p) continue;
      ctx.beginPath();
      ctx.arc(p[0], p[1], 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
}
